import React, { useState } from 'react';

import clsx from 'clsx';
import i18n from 'i18n-js';

import { Card, Checkbox, IconButton, Menu, MenuItem } from '@material-ui/core';
import PrintIcon from '@material-ui/icons/Print';
import { images } from 'assets';
import { PreOrderTag, WarmUpTag, PickupTimeslotTag } from 'components/shared';
import { theme } from 'config/theme';
import { OrderItemStatus, PrintType } from 'constants/enums';
import { priceStyleHelper } from 'helpers/helperFunctions';
import { useAppSelector } from 'index';
import { useDispatch } from 'react-redux';
import ordersStore from 'store/ordersStore';
import { OrderItem } from 'store/ordersStore/reducer';
import { useStyles } from '../styles';

interface OrderItemCardProps {
    dish: OrderItem;
    index: number;
    className: string | null;
    isFullDisabled: boolean;
    isDisabled: boolean;
    paddingBottom: number;
    isAllowed: boolean;
    isEditOrder: boolean;
    comment: JSX.Element | null;
    priceText: JSX.Element;
    addItemToOrder: (dish: OrderItem, amount: number) => void;
    handleCheckingItems: (
        id: string,
        uniqueId: string,
        status: OrderItemStatus,
        dailyMenuUniqueId?: string
    ) => void;
    highlitedText: (name: string) => JSX.Element;
    editedIngredients: JSX.Element | null;
    deliveredCheckbox: JSX.Element | undefined;
}

export default function OrderItemCard(props: OrderItemCardProps) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const orderDetails = useAppSelector((state) => state.orders.orderDetails);
    const [printItem, setPrintItem] = useState<null | string>(null);
    const [printAnchorEl, setPrintAnchorEl] = useState<null | HTMLElement>(null);

    const handlePrintClick = (event: React.MouseEvent<HTMLButtonElement>, item: string) => {
        setPrintAnchorEl(event.currentTarget);
        setPrintItem(item);
    };

    const handlePrintClose = () => {
        setPrintAnchorEl(null);
        setPrintItem(null);
    };

    const handlePrint = (printType: PrintType) => {
        if (printType === PrintType.WholeOrder) {
            dispatch(ordersStore.actionCreators.PrintOrder(orderDetails.id));
        } else if (printType === PrintType.SingleItem) {
            dispatch(ordersStore.actionCreators.PrintOrder(orderDetails.id, [printItem!]));
        }
        handlePrintClose();
    };

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
            }}
            key={props.dish.id + props.index}
        >
            <Card
                className={clsx(classes.orderItemCard, props.className, {
                    Disabled: props.isFullDisabled,
                })}
                style={{
                    paddingBottom: props.paddingBottom,
                }}
            >
                <div
                    style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        minHeight: '80px',
                    }}
                >
                    {props.isEditOrder ? (
                        <img
                            src={images.icons.removeCircle}
                            alt="remove-circle"
                            onClick={() => props.isAllowed && props.addItemToOrder(props.dish, 0)}
                            className={clsx(classes.removeOrderItem, {
                                Disabled: !props.isAllowed,
                            })}
                        />
                    ) : (
                        <Checkbox
                            onChange={() =>
                                props.handleCheckingItems(
                                    props.dish.id,
                                    props.dish.uniqueId!,
                                    props.dish.status === OrderItemStatus.Ready
                                        ? OrderItemStatus.Order
                                        : OrderItemStatus.Ready
                                )
                            }
                            inputProps={{ 'aria-label': 'select all desserts' }}
                            checked={
                                props.dish.status === OrderItemStatus.Ready ||
                                props.dish.status === OrderItemStatus.Closed ||
                                props.dish.status === OrderItemStatus.Delivered
                            }
                            disabled={props.isDisabled}
                        />
                    )}
                    <div className={classes.checkboxContainer}>
                        <div>
                            <div
                                className={clsx(classes.orderItemCardTitle, {
                                    [classes.orderItemCardTitleReady]:
                                        props.dish.status === OrderItemStatus.Ready,
                                })}
                            >
                                {props.isEditOrder
                                    ? props.dish.name
                                    : props.highlitedText(props.dish.name)}
                                {restaurant.advancedSettings?.isPrintingEnabled && (
                                    <IconButton
                                        style={{
                                            marginLeft: 10,
                                            color: props.className?.includes('orderItemCardReady')
                                                ? theme.palette.common.white
                                                : undefined,
                                        }}
                                        onClick={(e) => handlePrintClick(e, props.dish.uniqueId!)}
                                    >
                                        <PrintIcon />
                                    </IconButton>
                                )}
                            </div>
                            <div className="OrderItemCardDescription">
                                {!!props.dish.shortDescription
                                    ? props.dish.shortDescription
                                    : i18n.t('common.noDescription')}
                            </div>
                            {props.dish.isOrderInAdvance && !props.isEditOrder && (
                                <div className={classes.preOrderTagsContainer}>
                                    <PreOrderTag />
                                    <PickupTimeslotTag
                                        pickupTimeslot={props.dish.pickupTimeslot}
                                        pickupDate={props.dish.pickupDate}
                                    />
                                    <WarmUpTag warmUp={props.dish.warmUp} />
                                </div>
                            )}
                        </div>
                        {props.isEditOrder ? (
                            <div className={classes.orderItemAmountContainer}>
                                <div
                                    className={clsx(classes.orderItemCardPrice, {
                                        [classes.orderItemCardPriceReady]:
                                            props.dish.status === OrderItemStatus.Ready,
                                    })}
                                >
                                    <span style={{ paddingBottom: 2 }}>
                                        {i18n.t('common.currencies.chf')}
                                    </span>{' '}
                                    <span className={classes.itemPrice}>
                                        {priceStyleHelper(props.dish.price, true)}
                                    </span>{' '}
                                </div>
                                <div className={classes.orderItemAmount}>
                                    <span
                                        className={clsx(classes.orderItemAmountControl, {
                                            Disabled: props.dish.amount === 0 || !props.isAllowed,
                                        })}
                                        onClick={() =>
                                            props.dish.amount - 1 >= 0 &&
                                            props.addItemToOrder(props.dish, -1)
                                        }
                                    >
                                        -
                                    </span>
                                    <span
                                        className={clsx(classes.orderItemAmountNumber, {
                                            [classes.textLight]:
                                                props.dish.status === OrderItemStatus.Ready,
                                        })}
                                    >
                                        {props.dish.amount}
                                    </span>
                                    <span
                                        className={clsx(classes.orderItemAmountControl, {
                                            Disabled: !props.isAllowed,
                                        })}
                                        onClick={() => props.addItemToOrder(props.dish, 1)}
                                    >
                                        +
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div>
                                {props.priceText}
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent:
                                            props.dish.size && props.dish.amount > 1
                                                ? 'space-between'
                                                : 'flex-end',
                                        marginTop: orderDetails.isPaid ? 4 : 8,
                                    }}
                                >
                                    {props.dish.size && (
                                        <div
                                            className={clsx(classes.dishSize, {
                                                [classes.dishSizeReady]:
                                                    props.dish.status === OrderItemStatus.Ready,
                                            })}
                                        >
                                            {props.dish.size ?? ''}
                                        </div>
                                    )}
                                    {!props.isEditOrder && props.dish.amount > 1 && (
                                        <div
                                            className={clsx(classes.dishAmount, {
                                                [classes.dishAmountReady]:
                                                    props.dish.status === OrderItemStatus.Ready,
                                            })}
                                        >
                                            {props.dish.amount}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {props.editedIngredients}
                {props.comment}
            </Card>
            {props.deliveredCheckbox}
            <Menu
                id="simple-menu"
                anchorEl={printAnchorEl}
                keepMounted
                open={Boolean(printItem)}
                onClose={handlePrintClose}
            >
                <MenuItem onClick={() => handlePrint(PrintType.SingleItem)}>
                    {i18n.t('common.item')}
                </MenuItem>
                <MenuItem onClick={() => handlePrint(PrintType.WholeOrder)}>
                    {i18n.t('common.wholeOrder')}
                </MenuItem>
            </Menu>
        </div>
    );
}
